<template>
    <div v-visibilityhandler>
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <h3 class="title">Service Areas Dashboard</h3>
                        <service-areas-dashboard-filter-component
                            :selected-date="selectedDate"
                            @onFilterDashboard="handleFilterOrSearch"
                        />
                    </div>
                    <div class="custom-toolbar-end">
                        <search-component
                            v-if="$root.isDesktop"
                            :search-filter="currentSearchTextValue"
                            @onSearch="handleServiceAreaSearch"
                        />
                        <div v-if="isTeamOwner">
                            <router-link
                                :to="{
                                    name: 'Create service area'
                                }"
                            >
                                <md-button
                                    title="Add Service Area"
                                    class="md-primary md-just-icon md-round pull-right header-button"
                                >
                                    <md-icon>add</md-icon>
                                </md-button>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="md-layout">
            <div
                class="md-layout-item md-small-size-100 md-medium-size-100 md-layout md-large-size-66 md-xlarge-size-66"
            >
                <div class="md-layout">
                    <div class="md-layout-item margin-space-bot px-0" v-if="serviceAreasWithDeliveryWindows.length">
                        <div class="margin-space-bot">
                            <md-button
                                class="md-primary expand-btn ml-0"
                                @click="handleExpandAll"
                                v-if="!isLoading && serviceAreasWithDeliveryWindows.length"
                            >
                                Expand All
                            </md-button>
                        </div>
                        <md-list
                            class="service-area-schedules-body"
                            v-for="serviceAreaItem in serviceAreasWithDeliveryWindows"
                            :key="serviceAreaItem.serviceAreaId"
                        >
                            <md-list-item
                                md-expand
                                :md-ripple="false"
                                :md-expanded.sync="serviceAreaItem.isOpen"
                                @click.stop="handleServiceAreaItemClick(serviceAreaItem)"
                            >
                                <div
                                    :class="[
                                        'service-area-accordion-header',
                                        { 'service-area-open': serviceAreaItem.isOpen },
                                        serviceAreaItem.capacityBorder
                                    ]"
                                >
                                    <div
                                        v-if="serviceAreaItem.isCapacityDetailsLoading && !serviceAreaItem.isOpen"
                                        class="btn-loader"
                                    ></div>
                                    <div class="service-area-name">
                                        <span>
                                            {{ serviceAreaItem.name }}
                                        </span>
                                    </div>
                                    <div class="service-area-actions" v-if="isTeamOwner">
                                        <router-link
                                            :to="{
                                                name: 'Delivery Windows',
                                                params: { serviceAreaId: serviceAreaItem.serviceAreaId }
                                            }"
                                        >
                                            <div class="service-area-edit">
                                                <md-icon>edit</md-icon>
                                                <md-tooltip md-direction="left">Edit Service Area</md-tooltip>
                                            </div>
                                        </router-link>
                                    </div>
                                </div>
                                <md-list slot="md-expand">
                                    <div v-if="!serviceAreaItem.isCapacityDetailsLoading">
                                        <service-areas-dashboard-capacity-component
                                            :selected-date="selectedDate"
                                            :service-area-item="serviceAreaItem"
                                        />
                                    </div>
                                    <div v-else class="empty-container">
                                        <div class="table-loader" v-if="serviceAreaItem.isCapacityDetailsLoading">
                                            <fade-loader :loading="true" color="#333333" />
                                            <span>LOADING</span>
                                        </div>
                                    </div>
                                </md-list>
                            </md-list-item>
                        </md-list>
                    </div>
                    <div class="md-layout-item margin-space-bot" v-else>
                        <div class="empty-message" v-if="!isLoading">
                            <span class="text-center">No service areas available.</span>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="md-layout-item md-small-size-100 md-layout md-medium-size-100 md-large-size-33 md-xlarge-size-33"
            >
                <service-areas-dashboard-blockout-days-component
                    :is-loading="isLoading"
                    :blockout-days="blockoutDays"
                ></service-areas-dashboard-blockout-days-component>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import { SearchComponent } from '@/components';
import { DATE_TYPES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import ServiceAreasDashboardFilterComponent from './ServiceAreasDashboardFilterComponent';
import ServiceAreasDashboardCapacityComponent from './ServiceAreasDashboardCapacityComponent';
import ServiceAreasDashboardBlockoutDaysComponent from './ServiceAreasDashboardBlockoutDaysComponent';

export default {
    name: 'ServiceAreasDashboardOverview',
    components: {
        FadeLoader,
        SearchComponent,
        ServiceAreasDashboardFilterComponent,
        ServiceAreasDashboardCapacityComponent,
        ServiceAreasDashboardBlockoutDaysComponent
    },
    mixins: [GeneralMixin],
    data() {
        return {
            selectedDate: this.$route.params.date
                ? this.$route.params.date
                : moment().format(DATE_TYPES.internationalDate),
            currentSearchTextValue: null,
            serviceAreasWithDeliveryWindows: [],
            blockoutDays: []
        };
    },
    computed: {
        ...mapGetters({
            isLoading: 'isLoading',
            isTeamOwner: 'user/isTeamOwner'
        })
    },
    async mounted() {
        this.$router.replace({ path: `/settings/service-areas/dashboard/${this.selectedDate}` });

        if (!this.$root.isDesktop && this.$refs.mdDatePicker) {
            this.$refs.mdDatePicker.$refs.input.$el.setAttribute('readonly', true);
        }

        this.$_handleLoaderState(true);
        await this.refreshList();
        this.$_handleLoaderState(false);
    },
    methods: {
        async searchServiceAreasDeliveryWindows() {
            const qry = new URLSearchParams();
            if (this.selectedDate) {
                qry.append('selectedDate', this.selectedDate);
            }
            if (this.currentSearchTextValue) {
                qry.append('searchText', this.currentSearchTextValue);
            }
            const api = `/api/teams/service-areas/dashboard/search-delivery-windows?${qry}`;

            try {
                const { data } = await handleRequests(api);
                this.serviceAreasWithDeliveryWindows = data.serviceAreasWithDeliveryWindows.map((item) => ({
                    ...item,
                    isOpen: false,
                    capacityBorder: null,
                    capacityDetails: [],
                    isCapacityDetailsLoading: false
                }));
                this.blockoutDays = data.blockoutDays;
            } catch (error) {
                const message = 'Error in loading the service areas with schedules';
                showErrorMessage(this, message, error);
            }
        },
        async handleVisibilityChange(isHidden) {
            if (!isHidden) {
                await this.refreshList();
            }
        },
        async refreshList() {
            await this.searchServiceAreasDeliveryWindows();
            this.$nextTick(async () => {
                await this.getAllServiceAreaItemCapacityDetails(false);
            });
        },
        async handleExpandAll() {
            this.$nextTick(async () => {
                await this.getAllServiceAreaItemCapacityDetails(true);
            });
        },
        async getAllServiceAreaItemCapacityDetails(isOpen) {
            const concurrencyLimit = 2;
            const items = this.serviceAreasWithDeliveryWindows;
            const promises = [];
            for (let i = 0; i < items.length; i += concurrencyLimit) {
                const batch = items.slice(i, i + concurrencyLimit);
                promises.push(
                    ...batch.map(async (serviceAreaItem) => {
                        if (!serviceAreaItem.isOpen) {
                            serviceAreaItem.isOpen = isOpen;
                            if (!serviceAreaItem.capacityDetails || serviceAreaItem.capacityDetails.length === 0) {
                                await this.getServiceAreaItemCapacityDetails(serviceAreaItem);
                            }
                        }
                        return serviceAreaItem;
                    })
                );
            }
            await Promise.all(promises);
        },
        async handleServiceAreaItemClick(serviceAreaItem) {
            this.$nextTick(async () => {
                if (!serviceAreaItem.isOpen) {
                    if (serviceAreaItem.capacityDetails === null || serviceAreaItem.capacityDetails?.length === 0) {
                        await this.getServiceAreaItemCapacityDetails(serviceAreaItem);
                    }
                } else {
                    serviceAreaItem.capacityDetails = [];
                }
            });
        },
        async getServiceAreaItemCapacityDetails(serviceAreaItem) {
            if (serviceAreaItem.isCapacityDetailsLoading) {
                return;
            }

            serviceAreaItem.isCapacityDetailsLoading = true;
            const api = `/api/teams/service-areas/${serviceAreaItem.serviceAreaId}/dashboard/with-capacity-metrics/${
                this.selectedDate
            }`;

            try {
                serviceAreaItem.capacityDetails = {};
                const { data } = await handleRequests(api);
                serviceAreaItem.capacityDetails = { ...data };
                this.setServiceAreaCapacityBorder(serviceAreaItem);
            } catch (error) {
                const message = `Error in loading the service area ${serviceAreaItem.name} with capacity details`;
                showErrorMessage(this, message, error);
            }
            serviceAreaItem.isCapacityDetailsLoading = false;
        },
        setServiceAreaCapacityBorder(serviceAreaItem) {
            const capacityItems = serviceAreaItem?.capacityDetails?.deliveryWindowCapacityMetrics ?? [];

            if (capacityItems.length === 0) {
                serviceAreaItem.capacityBorder = null;
                return;
            }

            serviceAreaItem.capacityBorder = 'service-area-capacity-border-green';

            for (let i = 0; i < capacityItems.length; i++) {
                const x = capacityItems[i];

                if (!x.isTimeless) {
                    if (x.currentCapacity !== null && x.currentCapacity !== undefined) {
                        const ratio = (x.currentCapacity / x.maxCapacity) * 100;
                        if (ratio >= 90) {
                            serviceAreaItem.capacityBorder = 'service-area-capacity-border-red';
                            return;
                        }
                        if (ratio >= 70) {
                            serviceAreaItem.capacityBorder = 'service-area-capacity-border-yellow';
                        }
                    }
                }
            }
        },
        async handleFilterOrSearch(val) {
            if (this.selectedDate !== val.selectedDate) {
                this.selectedDate = val.selectedDate;

                this.$router.replace({ path: `/settings/service-areas/dashboard/${this.selectedDate}` });

                this.$nextTick(async () => {
                    this.serviceAreasWithDeliveryWindows = [];
                    this.blockoutDays = [];
                    this.$_handleLoaderState(true);
                    await this.refreshList();
                    this.$_handleLoaderState(false);
                });
            }
        },
        async handleServiceAreaSearch(val) {
            this.currentSearchTextValue = val.searchText;
            this.$nextTick(async () => {
                this.serviceAreasWithDeliveryWindows = [];
                this.blockoutDays = [];
                this.$_handleLoaderState(true);
                await this.refreshList();
                this.$_handleLoaderState(false);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.title {
    display: inline-block;
    margin-right: 10px;
}
::v-deep .header-button {
    width: 32px;
    height: 32px;
    min-width: 32px;
    margin-top: 0;
}

.service-area-schedules-body {
    margin: 0 0 10px 0;
    border-radius: 0;
    padding: 0;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);

    ::v-deep .md-list-item {
        margin-left: 0;
    }

    ::v-deep .md-list-item .md-list-item-container {
        font-weight: 100;

        a {
            font-weight: 100;
        }
    }

    ::v-deep .md-list-item .md-list-item-container .md-ripple {
        padding: 0;
        border-radius: 0;

        .md-list-expand-icon {
            margin-left: 0;
            margin-right: 10px;
            z-index: 1;
            position: absolute;
            right: 0;
        }
    }

    ::v-deep .md-list-item .md-list-item-container .md-ripple:hover {
        background-color: #fff !important;
    }

    ::v-deep .md-list-item .md-list-item-container {
        .alert-section,
        .stop-actions {
            .md-ripple:hover {
                background-color: initial !important;
            }
        }
    }

    ::v-deep .md-card-actions {
        button {
            height: 30px;
            min-width: 30px;
            width: 30px;
        }
    }

    ::v-deep .md-card-expand-content {
        border-top: 1px solid #eee;
    }
}

.service-area-accordion-header {
    height: 55px;
    padding-top: 20px;
    width: 100%;
    .service-area-name {
        font-size: 14px;
        width: 40%;
        text-transform: uppercase;
        font-weight: 600;
        display: inline-block;
        span {
            margin-left: 15px;
        }
    }
    .service-area-actions {
        float: right;
        margin-right: 50px;
    }
}

.service-area-open {
    border-bottom: 1px solid #eee;
}
.expand-btn {
    margin-left: 15px;
}

.empty-capacity-details {
    height: 150px;
    display: block;
}

.empty-container {
    height: 150px;
    display: block;
}
.service-area-capacity-border-red {
    border-left: 5px solid #eb4846;
}
.service-area-capacity-border-yellow {
    border-left: 5px solid #fb920d;
}
.service-area-capacity-border-green {
    border-left: 5px solid #4caf50;
}
.btn-loader {
    margin-top: 5px;
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
    float: left;
    margin-top: 0;
    margin-left: 5px;
}

.ml-0 {
    margin-left: 0 !important;
}

.px-0 {
    padding-left: 0 !important;
    padding-right: 0 !important;
}
</style>
